import { isProduction } from './environment-helper';
import StructuredDataHelper from './structured-data-helper';
import AdvancedMetaHelper from './advanced-meta-helper';

// SEO helper functions - only work in production
class SEOHelper {
  // Update page title
  static updateTitle(title) {
    if (!isProduction()) return;

    if (title) {
      document.title = title;
    }
  }

  // Update meta description
  static updateDescription(description) {
    if (!isProduction()) return;

    if (!description) return;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description;
  }

  // Update meta keywords
  static updateKeywords(keywords) {
    if (!isProduction()) return;

    if (!keywords) return;

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = Array.isArray(keywords) ? keywords.join(', ') : keywords;
  }

  // Update canonical URL
  static updateCanonical(url) {
    if (!isProduction()) return;

    if (!url) return;

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;
  }

  // Update Open Graph tags
  static updateOpenGraph(ogData) {
    if (!isProduction()) return;

    if (!ogData) return;

    const ogTags = {
      'og:title': ogData.title,
      'og:description': ogData.description,
      'og:image': ogData.image,
      'og:url': ogData.url,
      'og:type': ogData.type || 'website',
      'og:site_name': ogData.siteName || 'Coaching Search Engine',
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      if (!content) return;

      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.property = property;
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    });
  }

  // Update Twitter Card tags
  static updateTwitterCard(twitterData) {
    if (!isProduction()) return;

    if (!twitterData) return;

    const twitterTags = {
      'twitter:card': twitterData.card || 'summary_large_image',
      'twitter:title': twitterData.title,
      'twitter:description': twitterData.description,
      'twitter:image': twitterData.image,
      'twitter:site': twitterData.site || '@CoachingSearchEngine',
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      if (!content) return;

      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = name;
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    });
  }

  // Add structured data (JSON-LD)
  static updateStructuredData(structuredData) {
    if (!isProduction()) return;

    if (!structuredData) return;

    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((script) => script.remove());

    // Handle multiple structured data schemas
    if (Array.isArray(structuredData)) {
      // Add each schema as separate script tag
      structuredData.forEach((schema) => {
        if (schema) {
          const script = document.createElement('script');
          script.type = 'application/ld+json';
          script.textContent = JSON.stringify(schema);
          document.head.appendChild(script);
        }
      });
    } else {
      // Add single schema
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }

  // Complete SEO update for a page
  static updatePageSEO(seoData) {
    if (!isProduction()) return;

    if (!seoData) return;

    // Update basic meta tags
    this.updateTitle(seoData.title);
    this.updateDescription(seoData.description);
    this.updateKeywords(seoData.keywords);
    this.updateCanonical(seoData.canonical);

    // Update social media tags
    this.updateOpenGraph(seoData.openGraph);
    this.updateTwitterCard(seoData.twitterCard);

    // Update structured data
    this.updateStructuredData(seoData.structuredData);

    // Update advanced meta tags
    if (seoData.advancedMeta) {
      AdvancedMetaHelper.applyAdvancedMeta(seoData.advancedMeta);
    }
  }

  // Generate coach page SEO data
  static generateCoachSEO(coach, lang = 'tr') {
    if (!isProduction()) return null;

    if (!coach) return null;

    const coachName = `${coach.firstName} ${coach.lastName}`;
    const categories = coach.categories ? coach.categories.map((cat) => cat.name).join(', ') : '';
    const baseUrl = 'https://www.coachingsearchengine.com';
    const coachUrl = `${baseUrl}/${lang}/coach/${coach.slug}`;

    const title =
      lang === 'tr'
        ? `${coachName} - Profesyonel Koç | Coaching Search Engine`
        : `${coachName} - Professional Coach | Coaching Search Engine`;

    const description =
      lang === 'tr'
        ? `${coachName} ile profesyonel koçluk hizmeti alın. Uzmanlık alanları: ${categories}. Hemen iletişime geçin ve hedefinize ulaşın.`
        : `Get professional coaching services with ${coachName}. Specialties: ${categories}. Contact now and reach your goals.`;

    return {
      title,
      description,
      keywords: [coachName, 'koç', 'coach', 'koçluk', 'coaching', categories].filter(Boolean),
      canonical: coachUrl,
      openGraph: {
        title,
        description,
        image: coach.profileImage || `${baseUrl}/assets/default-coach.jpg`,
        url: coachUrl,
        type: 'profile',
        siteName: 'Coaching Search Engine',
      },
      twitterCard: {
        title,
        description,
        image: coach.profileImage || `${baseUrl}/assets/default-coach.jpg`,
      },
      structuredData: StructuredDataHelper.generateCoachPageStructuredData(coach, lang),
      advancedMeta: AdvancedMetaHelper.generateCoachMetaTags(coach, lang),
    };
  }

  // Generate category page SEO data
  static generateCategorySEO(category, lang = 'tr') {
    if (!isProduction()) return null;

    if (!category) return null;

    const categoryName = category.getName(lang);
    const categoryDescription = category.getDescription(lang);
    const baseUrl = 'https://www.coachingsearchengine.com';
    const categoryUrl = `${baseUrl}${category.getUrl(lang)}`;

    const title =
      lang === 'tr'
        ? `${categoryName} Koçları | Coaching Search Engine`
        : `${categoryName} Coaches | Coaching Search Engine`;

    const description =
      categoryDescription ||
      (lang === 'tr'
        ? `${categoryName} alanında uzman koçları keşfedin. Profesyonel koçluk hizmeti alın ve hedefinize ulaşın.`
        : `Discover expert coaches in ${categoryName}. Get professional coaching services and reach your goals.`);

    return {
      title,
      description,
      keywords: [categoryName, 'koç', 'coach', 'koçluk', 'coaching'],
      canonical: categoryUrl,
      openGraph: {
        title,
        description,
        image: `${baseUrl}/assets/category-${category.getSlug(lang)}.jpg`,
        url: categoryUrl,
        type: 'website',
        siteName: 'Coaching Search Engine',
      },
      twitterCard: {
        title,
        description,
        image: `${baseUrl}/assets/category-${category.getSlug(lang)}.jpg`,
      },
      structuredData: StructuredDataHelper.generateCategoryPageStructuredData(category, lang),
      advancedMeta: AdvancedMetaHelper.generateCategoryMetaTags(category, lang),
    };
  }

  // Generate homepage SEO data
  static generateHomepageSEO(lang = 'tr') {
    if (!isProduction()) return null;

    const baseUrl = 'https://www.coachingsearchengine.com';
    const homeUrl = `${baseUrl}/${lang}`;

    const title =
      lang === 'tr'
        ? 'Coaching Search Engine - AI Destekli Koç Keşif Platformu'
        : 'Coaching Search Engine - AI-Powered Coach Discovery Platform';

    const description =
      lang === 'tr'
        ? 'AI destekli platformumuzla size en uygun profesyonel koçları keşfedin. Kişiselleştirilmiş koçluk çözümleri için hemen arama yapın.'
        : 'Discover the most suitable professional coaches with our AI-powered platform. Search now for personalized coaching solutions.';

    return {
      title,
      description,
      keywords: ['coaching', 'koçluk', 'AI', 'artificial intelligence', 'professional coach', 'personal development'],
      canonical: homeUrl,
      openGraph: {
        title,
        description,
        image: `${baseUrl}/assets/homepage-og.jpg`,
        url: homeUrl,
        type: 'website',
        siteName: 'Coaching Search Engine',
      },
      twitterCard: {
        title,
        description,
        image: `${baseUrl}/assets/homepage-twitter.jpg`,
      },
      structuredData: StructuredDataHelper.generateHomepageStructuredData(lang),
      advancedMeta: AdvancedMetaHelper.generateHomepageMetaTags(lang),
    };
  }

  // Generate Facebook Data Deletion page SEO data
  static generateFacebookDataDeletionSEO(lang = 'tr') {
    if (!isProduction()) return null;

    const isEnglish = lang === 'en';
    const baseUrl = 'https://www.coachingsearchengine.com';

    const title = isEnglish
      ? 'Facebook Data Deletion Request - Coaching Search Engine'
      : 'Facebook Veri Silme Talebi - Coaching Search Engine';

    const description = isEnglish
      ? 'Request deletion of your Facebook data associated with Coaching Search Engine app. GDPR compliant data deletion process.'
      : 'Coaching Search Engine uygulaması ile ilişkili Facebook verilerinizin silinmesini talep edin. GDPR uyumlu veri silme süreci.';

    const pageUrl = isEnglish ? `${baseUrl}/en/facebook-data-deletion` : `${baseUrl}/tr/facebook-veri-silme`;

    return {
      title,
      description,
      keywords: isEnglish
        ? ['facebook data deletion', 'privacy rights', 'GDPR', 'personal data', 'user privacy']
        : ['facebook veri silme', 'gizlilik hakları', 'GDPR', 'kişisel veri', 'kullanıcı gizliliği'],
      canonical: pageUrl,
      openGraph: {
        title,
        description,
        url: pageUrl,
        type: 'website',
        siteName: 'Coaching Search Engine',
        image: `${baseUrl}/assets/privacy-og.jpg`,
      },
      twitterCard: {
        title,
        description,
        image: `${baseUrl}/assets/privacy-twitter.jpg`,
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description: description,
        url: pageUrl,
        inLanguage: lang,
        isPartOf: {
          '@type': 'WebSite',
          name: 'Coaching Search Engine',
          url: baseUrl,
        },
        mainEntity: {
          '@type': 'ContactPoint',
          contactType: 'Data Protection Officer',
          email: 'info@coachingsearchengine.com',
          availableLanguage: ['Turkish', 'English'],
          areaServed: ['TR', 'EN'],
          serviceType: 'Data Deletion Request',
        },
      },
    };
  }
}

export default SEOHelper;
